/**
 * Listas do teste depois da compra (matriz de funcionalidades, F7; decisão 293).
 *
 * No teste, o navegador guarda do que passou do limite de leitura só o fechado e a versão
 * embaralhada (chave `masked:`), e a lista ganha a marca `sealedAt`. Quando o limite cai (conta
 * ativa, `lists.readable_max` sem número), cada lista marcada com a cópia na nuvem em dia perde os
 * registros fechados e é lida de novo do servidor, que já guardou os originais. Lista com cópia
 * pendente espera a fila de backup: a mudança dela chama a releitura de novo.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clearListSealed, getLists, removeSealedRecords } from '../data/localDb';
import { subscribeLocalListChanges } from '../data/localListChanges';
import { limit, usesEntitlements, type EntitlementSession } from '../lib/entitlements';
import { hydrateListBackup } from '../lib/listHydration';
import { withListHydrationLock } from '../lib/listHydrationManager';
import type { ScanType } from '../types/searchList';

const running = new Set<string>();
const again = new Set<string>();
/** Listas que o servidor ainda devolveu mascaradas: só tentam de novo na próxima carga. */
const stillMasked = new Set<string>();

/** A conta comprou: os direitos valem, a conta está ativa e a leitura não tem limite. */
export function readableLimitLifted(session: EntitlementSession): boolean {
  return (
    usesEntitlements(session) &&
    session?.entitlements?.state === 'active' &&
    limit(session, 'lists.readable_max') === null
  );
}

export interface SealedListsRefresh {
  refreshed: Array<{ id: string; module: ScanType }>;
  /** Listas marcadas que esperam a cópia na nuvem. */
  pending: string[];
}

export async function refreshSealedLists(userId: string): Promise<SealedListsRefresh> {
  const out: SealedListsRefresh = { refreshed: [], pending: [] };
  if (!userId) return out;
  if (running.has(userId)) {
    again.add(userId);
    return out;
  }
  running.add(userId);
  const tried = new Set<string>();
  try {
    do {
      again.delete(userId);
      out.pending = [];
      for (const list of await getLists(userId)) {
        if (!list.sealedAt || tried.has(list.id) || stillMasked.has(list.id)) continue;
        if (list.backupState !== 'synced') {
          out.pending.push(list.id);
          continue;
        }
        tried.add(list.id);
        try {
          const done = await withListHydrationLock(list.id, userId, async () => {
            const removed = await removeSealedRecords(list.id);
            if (!removed.ok) return false;
            const result = await hydrateListBackup(list.id, { userId });
            if (result.sealed) {
              stillMasked.add(list.id);
              return false;
            }
            await clearListSealed(list.id);
            return true;
          });
          if (done) out.refreshed.push({ id: list.id, module: list.module });
        } catch {
          // Rede fora ou lista apagada no meio: a marca fica e a releitura volta na próxima carga.
        }
      }
    } while (again.has(userId));
  } finally {
    running.delete(userId);
  }
  return out;
}

export function useSealedListsRefresh(userId: string | undefined, lifted: boolean): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!userId || !lifted) return;
    let pending = new Set<string>();
    let active = true;
    const run = () => {
      refreshSealedLists(userId)
        .then((result) => {
          if (!active) return;
          pending = new Set(result.pending);
          for (const list of result.refreshed) {
            void queryClient.invalidateQueries({ queryKey: ['lists', list.module] });
            void queryClient.invalidateQueries({
              queryKey: [list.module === 'places' ? 'places' : 'leads', list.id],
            });
          }
        })
        .catch(() => undefined);
    };
    run();
    // Só as listas que esperavam a cópia na nuvem chamam a releitura de novo.
    const unsubscribe = subscribeLocalListChanges((change) => {
      if (pending.has(change.listId)) run();
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [userId, lifted, queryClient]);
}
